import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Server, Loader2, Shield, AlertTriangle, RefreshCw, Search, X, Crown, ChevronRight, Sparkles, Plus, History, Lock } from 'lucide-react';

export default function ServerSelector() {
  const { user, guilds, botOnlyGuilds, isAuthenticated, loading, login, error, retry, refreshGuilds } = useAuth();
  const [search, setSearch] = useState('');

  // Persist and load recently visited server IDs from localStorage
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('anya_recent_servers');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleGuildClick = (guildId: string) => {
    setRecentIds((prev) => {
      const filtered = prev.filter((id) => id !== guildId);
      const updated = [guildId, ...filtered].slice(0, 3); // Keep only top 3 recents
      localStorage.setItem('anya_recent_servers', JSON.stringify(updated));
      return updated;
    });
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return guilds;
    const q = search.toLowerCase();
    return guilds.filter((g) => g.name.toLowerCase().includes(q));
  }, [guilds, search]);

  const filteredBotOnly = useMemo(() => {
    if (!search.trim()) return botOnlyGuilds;
    const q = search.toLowerCase();
    return botOnlyGuilds.filter((g) => g.name.toLowerCase().includes(q));
  }, [botOnlyGuilds, search]);

  // Separate guilds into Manageable (hasBot = true) and Invite (hasBot = false/undefined)
  const { activeGuilds, inviteGuilds } = useMemo(() => {
    const active = filtered.filter((g) => g.hasBot);
    const invite = filtered.filter((g) => !g.hasBot);
    return { activeGuilds: active, inviteGuilds: invite };
  }, [filtered]);

  // Resolve recent guilds from active guilds (only show recent manageable guilds that exist)
  const recentGuilds = useMemo(() => {
    return recentIds
      .map((id) => guilds.find((g) => g.id === id))
      .filter((g): g is NonNullable<typeof g> => !!g && !!g.hasBot);
  }, [recentIds, guilds]);

  const BOT_INVITE_URL = (guildId: string) => {
    const clientId = '1234247716243112100'; // Default Anya Bot client ID
    return `https://discord.com/oauth2/authorize?client_id=${clientId}&permissions=8&scope=bot%20applications.commands&guild_id=${guildId}&disable_guild_select=true`;
  };

  if (loading && guilds.length === 0) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-primary animate-spin" />
            </div>
            <div className="absolute inset-0 rounded-2xl bg-primary/10 blur-xl" />
          </div>
          <div className="text-center">
            <p className="text-white font-medium">Loading servers...</p>
            <p className="text-gray-500 text-sm mt-0.5">Fetching your Discord guilds</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
              <button
                onClick={retry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-sm font-medium rounded-xl transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          )}

          {/* Hero icon */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/20 via-secondary/15 to-accent-purple/20 flex items-center justify-center border border-primary/20">
              <Shield className="w-11 h-11 text-primary" />
            </div>
            <div className="absolute inset-0 rounded-3xl bg-primary/10 blur-2xl" />
          </div>

          <h1 className="text-4xl font-display font-bold text-white mb-3 tracking-tight">
            Server Dashboard
          </h1>
          <p className="text-gray-400 mb-2 leading-relaxed">
            Connect your Discord account to manage servers, configure welcome messages, auto-mod, Pokémon settings, and more.
          </p>

          <div className="flex flex-wrap gap-2 justify-center mb-8 mt-4">
            {['Welcome', 'Moderation', 'Leveling', 'Pokémon'].map((f) => (
              <span key={f} className="text-xs px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-gray-400">
                {f}
              </span>
            ))}
          </div>

          <button
            onClick={login}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-2xl transition-all hover:shadow-xl hover:shadow-[#5865F2]/30 hover:-translate-y-0.5 active:scale-[0.97] w-full justify-center"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
            </svg>
            Continue with Discord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            {user?.avatar ? (
              <div className="relative">
                <img src={user.avatar} alt="" className="w-12 h-12 rounded-2xl ring-2 ring-primary/25" />
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-dark-900" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <p className="text-gray-400 text-sm">Welcome back</p>
              <h1 className="text-2xl font-display font-bold text-white tracking-tight">
                {user?.displayName}
              </h1>
            </div>
          </div>
        </div>

        {/* Search + count row */}
        <div className="flex items-center gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search servers…"
              className="w-full pl-10 pr-10 py-2.5 bg-dark-800/60 border border-white/[0.07] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-primary/40 focus:bg-dark-800 transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={refreshGuilds}
            disabled={loading}
            title="Refresh servers list"
            className="flex items-center justify-center p-2.5 bg-dark-800/60 hover:bg-dark-800 border border-white/[0.07] hover:border-primary/30 rounded-xl text-gray-400 hover:text-white transition-all disabled:opacity-40 shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>

          <div className="shrink-0 text-sm text-gray-500 tabular-nums">
                      {search
              ? <span><span className="text-white font-medium">{filtered.length + filteredBotOnly.length}</span> / {guilds.length + botOnlyGuilds.length}</span>
              : <span><span className="text-white font-medium">{guilds.length + botOnlyGuilds.length}</span> server{(guilds.length + botOnlyGuilds.length) !== 1 ? 's' : ''}</span>
            }
          </div>
        </div>

        {/* Recently Visited Section */}
        {recentGuilds.length > 0 && !search && (
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <History className="w-3.5 h-3.5 text-primary/70 animate-pulse" />
              Recently Visited
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {recentGuilds.map((guild) => (
                <Link
                  key={guild.id}
                  to={`/dashboard/${guild.id}`}
                  onClick={() => handleGuildClick(guild.id)}
                  className="group relative flex items-center gap-3.5 p-4 rounded-2xl bg-gradient-to-br from-primary/5 via-dark-800/40 to-dark-800/40 hover:bg-dark-800/80 border border-primary/20 hover:border-primary/40 transition-all duration-200 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5"
                >
                  {/* Glowing background */}
                  <div className="absolute inset-0 rounded-2xl bg-primary/5 opacity-40 group-hover:opacity-100 blur-xl transition-all duration-500 pointer-events-none" />

                  {/* Server icon */}
                  <div className="relative shrink-0 z-10">
                    {guild.icon ? (
                      <img
                        src={guild.icon}
                        alt={guild.name}
                        className="w-12 h-12 rounded-xl group-hover:ring-2 ring-primary/40 transition-all"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dark-700 to-dark-600 flex items-center justify-center text-white font-bold text-lg group-hover:ring-2 ring-primary/40 transition-all">
                        {guild.name.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Name + details */}
                  <div className="min-w-0 flex-1 relative z-10">
                    <p className="text-white font-semibold text-sm truncate group-hover:text-primary transition-colors duration-150">
                      {guild.name}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[10px] text-primary mt-0.5">
                      <Sparkles className="w-3 h-3 text-primary" />
                      Manage Server
                    </span>
                  </div>

                  <ChevronRight className="w-4 h-4 text-primary/70 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0 relative z-10" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Guild lists layout */}
        {filtered.length === 0 && filteredBotOnly.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-dark-800/60 border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
              <Server className="w-7 h-7 text-gray-600" />
            </div>
            {search ? (
              <>
                <p className="text-white font-semibold mb-1">No results for "{search}"</p>
                <p className="text-gray-500 text-sm">Try searching by a different name</p>
              </>
            ) : (
              <>
                <p className="text-white font-semibold mb-1">No servers found</p>
                <p className="text-gray-500 text-sm max-w-xs mx-auto">
                  Make sure Anya Bot is in at least one of your servers.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Manageable Servers list */}
            <div>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-green-500/70" />
                Manageable Servers ({activeGuilds.length})
              </h2>
              {activeGuilds.length === 0 ? (
                <div className="p-6 bg-dark-800/30 rounded-2xl border border-white/[0.04] text-center text-sm text-gray-500">
                  No active servers. Invite Anya to one of your servers below!
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {activeGuilds.map((guild) => (
                    <Link
                      key={guild.id}
                      to={`/dashboard/${guild.id}`}
                      onClick={() => handleGuildClick(guild.id)}
                      className="group relative flex items-center gap-3.5 p-4 rounded-2xl bg-dark-800/40 hover:bg-dark-800/80 border border-white/[0.06] hover:border-primary/25 transition-all duration-200 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5"
                    >
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-300 pointer-events-none" />
                      <div className="relative shrink-0">
                        {guild.icon ? (
                          <img
                            src={guild.icon}
                            alt={guild.name}
                            className="w-12 h-12 rounded-xl group-hover:ring-2 ring-primary/30 transition-all"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dark-700 to-dark-600 flex items-center justify-center text-white font-bold text-lg group-hover:ring-2 ring-primary/30 transition-all">
                            {guild.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 relative">
                        <p className="text-white font-semibold text-sm truncate group-hover:text-primary transition-colors duration-150">
                          {guild.name}
                        </p>
                        {guild.owner ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-amber-400/80 mt-0.5">
                            <Crown className="w-3 h-3" />
                            Owner
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-0.5">
                            <Shield className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-700 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0 relative" />
                    </Link>
                  ))}
                </div>
              )}
            </div>

                        {/* Bot-present but no-admin section */}
            {filteredBotOnly.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 text-gray-600" />
                  Anya is here — No Admin Access ({filteredBotOnly.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {filteredBotOnly.map((guild) => (
                    <div
                      key={guild.id}
                      title="You need Manage Server permission to configure this server"
                      className="relative flex items-center gap-3.5 p-4 rounded-2xl bg-dark-800/20 border border-white/[0.03] cursor-not-allowed select-none"
                    >
                      {/* Subtle lock overlay */}
                      <div className="absolute inset-0 rounded-2xl" />

                      <div className="relative shrink-0">
                        {guild.icon ? (
                          <img
                            src={guild.icon}
                            alt={guild.name}
                            className="w-12 h-12 rounded-xl opacity-30 grayscale"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dark-800 to-dark-700 flex items-center justify-center text-gray-700 font-bold text-lg opacity-40">
                            {guild.name.charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-gray-600 font-semibold text-sm truncate">
                          {guild.name}
                        </p>
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-700 mt-0.5">
                          <Lock className="w-3 h-3" />
                          No admin access
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invite Servers list */}
            {inviteGuilds.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Plus className="w-3.5 h-3.5 text-primary/70" />
                  Add Anya Bot to Servers ({inviteGuilds.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {inviteGuilds.map((guild) => (
                    <a
                      key={guild.id}
                      href={BOT_INVITE_URL(guild.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center gap-3.5 p-4 rounded-2xl bg-dark-800/10 hover:bg-dark-800/40 border border-white/[0.02] hover:border-primary/20 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <div className="relative shrink-0">
                        {guild.icon ? (
                          <img
                            src={guild.icon}
                            alt={guild.name}
                            className="w-12 h-12 rounded-xl group-hover:ring-2 ring-primary/20 transition-all opacity-60 group-hover:opacity-100"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dark-800 to-dark-700 flex items-center justify-center text-gray-500 font-bold text-lg group-hover:ring-2 ring-primary/20 transition-all">
                            {guild.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 relative">
                        <p className="text-gray-400 font-semibold text-sm truncate group-hover:text-white transition-colors duration-150">
                          {guild.name}
                        </p>
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 group-hover:text-primary transition-colors mt-0.5">
                          Click to Setup
                        </span>
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Plus className="w-4 h-4" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
